import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'phosphor-react';
import moment from 'moment';
import { ArAgingRow } from './types';
import { exportToXlsx, exportToCsv } from './exportUtils';
import { Export, Eye } from '@phosphor-icons/react';
import { DillButton, DillModal, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getCustomerCollectionViews } from '../../../../../redux/services/customerCollectionViewsService';
import CollectionActivityTimeline from '../../CollectionActivityTimeline/CollectionActivityTimeline';
import {
  AgingBucketKey,
  StoredQuickFilters,
  BUCKET_QUICK_FILTERS,
  PROMISE_TO_PAY_QUICK_FILTERS,
  FLAG_QUICK_FILTERS,
  formatAmountRangeLabel,
} from '../../../customers/Customers/CollectionViews';

// Maps a Collection View's aging-bucket keys (shared with the Customers tab's quick filters) to
// this report's own field names for the same buckets.
const BUCKET_KEY_TO_AR_FIELD: Record<AgingBucketKey, keyof ArAgingRow> = {
  pastDue90: 'pastDueOver90',
  pastDue61: 'pastDue61To90',
  pastDue31: 'pastDue31To60',
  pastDue1: 'pastDue1To30',
};

// Applies a saved Collection View's filter criteria to this report's rows - same group
// semantics as the Customers tab's quickFilteredRows (OR within a group, AND across groups),
// just re-targeted at this report's row shape (e.g. `creditManagerId` instead of a resolved
// name, and the fixed pastDueXToY fields instead of the generic bucket keys).
const applyCollectionViewFilters = (
  rows: ArAgingRow[],
  filters: StoredQuickFilters
): ArAgingRow[] => {
  const isActive =
    filters.buckets.length > 0 ||
    filters.branches.length > 0 ||
    filters.creditManagerIds.length > 0 ||
    filters.promiseToPay.length > 0 ||
    filters.flagged.length > 0;
  if (!isActive) return rows;

  return rows.filter((row) => {
    if (filters.buckets.length > 0) {
      const matchesABucket = filters.buckets.some((key) => {
        const amount = row[BUCKET_KEY_TO_AR_FIELD[key]] as number;
        if (!(amount > 0)) return false;
        const range = filters.bucketAmountRanges?.[key];
        if (range?.min != null && amount < range.min) return false;
        if (range?.max != null && amount > range.max) return false;
        return true;
      });
      if (!matchesABucket) return false;
    }
    if (filters.branches.length > 0) {
      if (!row.branch || !filters.branches.includes(row.branch)) return false;
    }
    if (filters.creditManagerIds.length > 0) {
      if (!row.creditManagerId || !filters.creditManagerIds.includes(row.creditManagerId)) {
        return false;
      }
    }
    if (filters.promiseToPay.length > 0) {
      const matchesPromise = filters.promiseToPay.some((value) =>
        value === 'HAS_ACTIVE' ? row.hasActivePromiseToPay : !row.hasActivePromiseToPay
      );
      if (!matchesPromise) return false;
    }
    if (filters.flagged.length > 0) {
      if (!row.isFlagged) return false;
    }
    return true;
  });
};

// Same pill styling as the Customers tab's Collection View tabs (see tabClass in
// CollectionViewTabs, CollectionViews.tsx) - kept as a local copy rather than exported, since
// this report only needs the read-only switcher look, not that component's create/rename/delete
// affordances (views are managed from the Customers tab).
const viewPillClass = (isActive: boolean) =>
  `px-3 py-1.5 text-xs font-semibold rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
    isActive
      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
  }`;

// Human-readable labels for collection activity types, shown in the "Collections Activity"
// column (mirrors the Customers table's mapping — see CollectionActivityTimeline.tsx for the
// fuller icon/color config).
const COLLECTION_ACTIVITY_TYPE_LABELS: Record<string, string> = {
  PAYMENT_REQUEST: 'Payment Request',
  PAYMENT_REMINDER: 'Payment Reminder',
  PAYMENT: 'Payment',
  REFUND: 'Refund',
  STATEMENT: 'Statement',
  DISPUTE: 'Dispute',
  PHONE_CALL: 'Phone Call',
  TEXT_MESSAGE: 'Text Message',
  NOTE: 'Note',
  TASK: 'Task',
  PROMISE_TO_PAY: 'Promise to Pay',
};

// ─── Column helpers ───────────────────────────────────────────────────────────

const getColorClass = (
  riskColor?: 'green' | 'yellow' | 'orange' | 'red' | 'neutral' | 'bold'
): string => {
  switch (riskColor) {
    case 'green':
      return 'text-green-600';
    case 'yellow':
      return 'text-yellow-600';
    case 'orange':
      return 'text-orange-600';
    case 'red':
      return 'text-red-600';
    case 'neutral':
      return 'text-gray-700';
    case 'bold':
      return 'text-gray-900 font-semibold';
    default:
      return 'text-gray-700';
  }
};

const textColumn = (
  key: keyof ArAgingRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<ArAgingRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const booleanColumn = (
  key: keyof ArAgingRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<ArAgingRow> => ({
  key,
  label,
  width: options?.width ?? 150,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: boolean) =>
    value ? (
      <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700">
        Yes
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
        No
      </span>
    ),
});

const numericColumn = (
  key: keyof ArAgingRow,
  label: string,
  riskColor?: 'green' | 'yellow' | 'orange' | 'red' | 'neutral' | 'bold',
  width = 150
): DillColumn<ArAgingRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-xs text-gray-300 text-right">—</div>;
    }

    const colorClass = getColorClass(riskColor);
    return (
      <div className={`text-xs font-medium text-right tabular-nums ${colorClass}`}>
        {value < 0 ? '-' : ''}$
        {Math.abs(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

// ─── Emails tooltip (mirrors Customers table pattern) ─────────────────────────

function EmailsTooltip({
  emails,
  children,
}: {
  emails: string[];
  children: (props: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    ref: React.RefObject<HTMLDivElement>;
  }) => React.ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    arrowTop: 0,
    side: 'right' as 'right' | 'left',
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleTriggerMouseEnter = () => {
    clearHideTimeout();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const TOOLTIP_W = 280;
      const isLeft = rect.right + 8 + TOOLTIP_W > window.innerWidth;
      const left = isLeft ? rect.left - TOOLTIP_W - 8 : rect.right + 8;
      const estimatedH = emails.length * 40 + 40;
      const rawTop = rect.top;
      const top = Math.max(8, Math.min(rawTop, window.innerHeight - estimatedH - 8));
      setPosition({
        top,
        left,
        arrowTop: rect.top - top + rect.height / 2,
        side: isLeft ? 'left' : 'right',
      });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => () => clearHideTimeout(), []);

  return (
    <>
      {children({
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleMouseLeave,
        ref: triggerRef,
      })}
      {isHovered &&
        createPortal(
          <div
            className="fixed bg-white border border-gray-100 shadow-2xl rounded-2xl py-3 px-3 z-[99999] w-[280px] overflow-y-auto"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              maxHeight: `calc(100vh - ${position.top}px - 8px)`,
            }}
            onMouseEnter={clearHideTimeout}
            onMouseLeave={handleMouseLeave}>
            <div
              className="absolute w-3 h-3 bg-white border-gray-100"
              style={{
                top: position.arrowTop - 6,
                ...(position.side === 'right'
                  ? {
                      left: -6,
                      borderLeftWidth: 1,
                      borderBottomWidth: 1,
                      transform: 'rotate(45deg)',
                    }
                  : {
                      right: -6,
                      borderRightWidth: 1,
                      borderTopWidth: 1,
                      transform: 'rotate(45deg)',
                    }),
              }}
            />
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5 px-1">
              {emails.length} email{emails.length !== 1 ? 's' : ''}
            </p>
            <div className="flex flex-col gap-2">
              {emails.map((email, i) => (
                <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
                  <p className="text-xs font-medium text-gray-700 break-all leading-tight">
                    {email}
                  </p>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

// ─── Column definitions ────────────────────────────────────────────────────────

const getColumns = ({
  showCODCustomer,
  onViewCollectionActivity,
}: {
  showCODCustomer: boolean;
  onViewCollectionActivity: (row: ArAgingRow) => void;
}): DillColumn<ArAgingRow>[] => [
  textColumn('quickbooksCustomerId', 'Customer A/R #', { width: 140 }),
  textColumn('businessName', 'Business Name', { width: 180 }),
  textColumn('branch', 'Branch', { width: 120 }),
  textColumn('streetAddress', 'Street Address', { defaultHidden: true, width: 180 }),
  textColumn('city', 'City', { defaultHidden: true, width: 120 }),
  textColumn('state', 'State', { defaultHidden: true, width: 80 }),
  textColumn('zipCode', 'Zip Code', { defaultHidden: true, width: 100 }),
  textColumn('phoneNumber', 'Phone Number', { defaultHidden: true, width: 140 }),
  {
    key: 'emails',
    label: (
      <span className="flex items-center gap-1">
        Emails
        <Info
          size={12}
          className="text-gray-400 cursor-pointer shrink-0"
          data-tooltip-id="emailsHeaderInfo"
        />
      </span>
    ),
    width: 200,
    sortable: true,
    filterable: true,
    toggleable: true,
    defaultHidden: true,
    render: (value: string) => {
      if (!value) return <span className="text-gray-300 text-xs">—</span>;
      const emails = value
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      if (emails.length === 0) return <span className="text-gray-300 text-xs">—</span>;
      return (
        <EmailsTooltip emails={emails}>
          {({ onMouseEnter, onMouseLeave, ref }) => (
            <div
              ref={ref}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className="flex items-center gap-1.5 cursor-pointer group w-full min-w-0">
              <span className="text-xs text-gray-700 truncate flex-1 min-w-0">{emails[0]}</span>
              <div className="flex items-center gap-0.5 shrink-0">
                {emails.length > 1 && (
                  <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 font-medium ml-0.5">
                    +{emails.length - 1}
                  </span>
                )}
              </div>
            </div>
          )}
        </EmailsTooltip>
      );
    },
  },
  ...(showCODCustomer ? [booleanColumn('isCODCustomer', 'COD Customer', { width: 150 })] : []),
  textColumn('terms', 'Terms', { defaultHidden: true, width: 120 }),
  textColumn('lastLogin', 'Last Login', { defaultHidden: true, width: 120 }),
  textColumn('creditUtilized', 'Credit Utilized', { defaultHidden: true, width: 130 }),
  numericColumn('balance', 'Balance', 'bold', 140),
  numericColumn('current', 'Current', 'neutral', 130),
  numericColumn('pastDue1To30', '1-30 Days Past Due', 'green', 160),
  numericColumn('pastDue31To60', '31-60 Days Past Due', 'yellow', 160),
  numericColumn('pastDue61To90', '61-90 Days Past Due', 'orange', 160),
  numericColumn('pastDueOver90', '91+ Days Past Due', 'red', 160),
  numericColumn('totalPastDue', 'Total Past Due', 'red', 150),
  {
    key: 'pastDuePct',
    label: '% of Total Past Due',
    width: 150,
    sortable: true,
    filterable: true,
    numeric: true,
    toggleable: true,
    headerAlign: 'right',
    render: (value: number) =>
      value > 0 ? (
        <div className="text-xs font-medium text-right tabular-nums text-red-500">
          {value.toFixed(1)}%
        </div>
      ) : (
        <div className="text-xs text-gray-300 text-right">—</div>
      ),
  },
  {
    key: 'isBuyerOnCreditHold',
    label: 'On Credit Hold',
    width: 140,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: boolean) =>
      value ? (
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
          Yes
        </span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
          No
        </span>
      ),
  },
  textColumn('creditManagerName', 'Credit Manager', { defaultHidden: true, width: 160 }),
  booleanColumn('hasActivePromiseToPay', 'Promise to Pay', { defaultHidden: true, width: 150 }),
  booleanColumn('isFlagged', 'Flagged', { defaultHidden: true, width: 120 }),
  {
    key: 'lastCollectionActivity',
    label: 'Collections Activity',
    width: 170,
    sortable: true,
    toggleable: true,
    getSortValue: (row: ArAgingRow) =>
      row.lastCollectionActivity?.date ? new Date(row.lastCollectionActivity.date).getTime() : 0,
    render: (_: any, row: ArAgingRow) =>
      row.lastCollectionActivity?.date ? (
        <button
          className="group flex flex-col w-full justify-center cursor-pointer rounded-lg border border-transparent px-2 py-1 hover:bg-blue-50 hover:border-blue-100 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onViewCollectionActivity(row);
          }}>
          <p className="flex items-center justify-center gap-1 text-xs text-blue-600 group-hover:underline text-center w-full">
            {moment(row.lastCollectionActivity.date).format('MM/DD/YYYY')}
            {row.lastCollectionActivity.type
              ? ` · ${
                  COLLECTION_ACTIVITY_TYPE_LABELS[row.lastCollectionActivity.type] ??
                  row.lastCollectionActivity.type
                }`
              : ''}
            <Eye size={12} className="text-blue-300 group-hover:text-blue-500 shrink-0" />
          </p>
          <p className="text-xs text-gray-500 text-center w-full">
            {row.lastCollectionActivity.countLast30Days} in last 30 days
          </p>
        </button>
      ) : (
        <div className="flex flex-col w-full justify-center">
          <span className="text-gray-200 text-center w-full">—</span>
        </div>
      ),
  },
  {
    key: 'lastPaymentReceived',
    label: 'Last Payment Received',
    width: 170,
    sortable: true,
    toggleable: true,
    headerAlign: 'right',
    getSortValue: (row: ArAgingRow) =>
      row.lastPaymentReceived?.date ? new Date(row.lastPaymentReceived.date).getTime() : 0,
    render: (_: any, row: ArAgingRow) =>
      row.lastPaymentReceived?.date ? (
        <div className="flex flex-col items-end w-full">
          <span className="text-xs font-medium text-gray-700 tabular-nums">
            $
            {row.lastPaymentReceived.amount.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <span className="text-[10px] text-gray-400">
            {moment(row.lastPaymentReceived.date).format('MM/DD/YYYY')}
          </span>
        </div>
      ) : (
        <div className="text-xs text-gray-300 text-right">—</div>
      ),
  },
  {
    key: 'collectionStatus',
    label: 'In Sequence/Trigger',
    width: 150,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: ArAgingRow['collectionStatus']) => {
      if (value === 'Sequence') {
        return (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
            Sequence
          </span>
        );
      }
      if (value === 'Trigger') {
        return (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
            Trigger
          </span>
        );
      }
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
          No
        </span>
      );
    },
  },
  textColumn('collectionName', 'Sequence/Trigger Name', { width: 200 }),
  {
    key: 'collectionStep',
    label: 'Sequence Step',
    width: 220,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: string) => (
      <div className="text-xs text-gray-500 line-clamp-2" title={value || undefined}>
        {value || '—'}
      </div>
    ),
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface ArAgingTableProps {
  data: ArAgingRow[];
  isLoading?: boolean;
  totalCount?: number;
}

export const ArAgingTable = ({ data, isLoading, totalCount }: ArAgingTableProps) => {
  const dispatch = useAppDispatch();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Rows after the table's search/filters/sorting are applied — what we export.
  const [filteredData, setFilteredData] = useState<ArAgingRow[]>(data);
  const [collectionActivityRow, setCollectionActivityRow] = useState<ArAgingRow | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const showCODCustomer = currentSupplier?.integrationType === 'ECLIPSE';

  // Collection Views (private per user, shared with the Customers tab - see
  // customerCollectionViewsSlice) - lets this report be filtered by a saved view (e.g. "Branch X
  // 91+") instead of setting up the same branch/bucket/credit-manager/etc. filters by hand.
  const { views: collectionViews } = useAppSelector((s) => s.customerCollectionViews);
  const [selectedViewId, setSelectedViewId] = useState('');
  const selectedView = collectionViews.find((v) => v.id === selectedViewId) ?? null;

  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getCustomerCollectionViews({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id, dispatch]);

  const viewFilteredData = useMemo(
    () => (selectedView ? applyCollectionViewFilters(data, selectedView.filters) : data),
    [data, selectedView]
  );

  // Resolves a view's stored credit manager ids to display names for the chips below - built
  // from the already-loaded rows rather than a separate supplierUsers fetch.
  const creditManagerNameById = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((row) => {
      if (row.creditManagerId && row.creditManagerName) {
        map.set(row.creditManagerId, row.creditManagerName);
      }
    });
    return map;
  }, [data]);

  // Read-only pills describing the selected view's filter criteria - unlike the Customers tab's
  // chips, these aren't individually removable (views are managed from there, not here).
  const activeViewFilterChips = useMemo(() => {
    if (!selectedView) return [];
    const filters = selectedView.filters;
    const chips: { id: string; label: string }[] = [];
    filters.buckets.forEach((key) => {
      const label = BUCKET_QUICK_FILTERS.find((b) => b.key === key)?.label ?? key;
      const range = filters.bucketAmountRanges?.[key];
      const rangeLabel = range ? formatAmountRangeLabel(range) : '';
      chips.push({ id: `bucket:${key}`, label: rangeLabel ? `${label} (${rangeLabel})` : label });
    });
    filters.branches.forEach((branch) =>
      chips.push({ id: `branch:${branch}`, label: `Branch: ${branch}` })
    );
    filters.creditManagerIds.forEach((id) =>
      chips.push({
        id: `creditManager:${id}`,
        label: `Credit Manager: ${creditManagerNameById.get(id) ?? id}`,
      })
    );
    filters.promiseToPay.forEach((value) =>
      chips.push({
        id: `promiseToPay:${value}`,
        label: PROMISE_TO_PAY_QUICK_FILTERS.find((p) => p.value === value)?.label ?? value,
      })
    );
    filters.flagged.forEach((value) =>
      chips.push({
        id: `flagged:${value}`,
        label: FLAG_QUICK_FILTERS.find((f) => f.value === value)?.label ?? value,
      })
    );
    return chips;
  }, [selectedView, creditManagerNameById]);

  const allColumns = useMemo(
    () => getColumns({ showCODCustomer, onViewCollectionActivity: setCollectionActivityRow }),
    [showCODCustomer]
  );

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Don't allow fewer than 3 visible columns
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };

    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen, isColumnsOpen]);

  const exportColumns = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.key,
        label: typeof c.label === 'string' ? c.label : c.key,
      })),
    [visibleColumns]
  );

  const handleExportXlsx = () => {
    exportToXlsx(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportToCsv(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6  flex flex-col">
      {collectionViews.length > 0 && (
        <div className="mb-3 flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => setSelectedViewId('')}
            className={viewPillClass(selectedViewId === '')}>
            All Customers
          </button>
          {collectionViews.map((view) => (
            <button
              key={view.id}
              type="button"
              onClick={() => setSelectedViewId(view.id)}
              className={viewPillClass(selectedViewId === view.id)}>
              {view.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4">
        <p className="text-sm text-gray-500">
          Find AR Aging reports below, where you can filter, sort, and adjust columns. Export to
          Excel or CSV.
        </p>
      </div>

      <div className={`h-[calc(100vh-260px)]`}>
        <DillTable<ArAgingRow>
          columns={visibleColumns}
          data={viewFilteredData}
          onProcessedDataChange={setFilteredData}
          rowKey={(r) => r.id}
          verticalDividers
          search
          rowHeight={35}
          searchPlaceholder="Search customers…"
          pagination
          pageSize={25}
          totalCount={totalCount}
          scrollX
          striped={false}
          emptyMessage="No customers match your filters"
          showCount
          loadingMore={isLoading}
          showFooter={false}
          virtualize
          showActiveFilters
          belowToolbar={
            activeViewFilterChips.length > 0 ? (
              <div className="flex items-center gap-2 flex-wrap border-b border-gray-100">
                <span className="text-xs text-gray-400 ml-1">
                  Active Filters ({selectedView?.name}):
                </span>
                {activeViewFilterChips.map((chip) => (
                  <span
                    key={chip.id}
                    className="inline-flex items-center pl-2.5 pr-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    {chip.label}
                  </span>
                ))}
              </div>
            ) : undefined
          }
          toolbarRight={
            <div className="flex gap-2">
              {/* Column visibility dropdown */}
              <div className="relative" ref={columnsRef}>
                <DillButton
                  label="Columns"
                  onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                  icon={<Eye size={14} />}
                  variant="ghost"
                />

                {isColumnsOpen && (
                  <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
                      <span>Show/Hide Columns</span>
                      <button
                        onClick={() => setHiddenCols(new Set())}
                        className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
                        Select All
                      </button>
                    </div>
                    {allColumns.map((col) => {
                      const isVisible = !hiddenCols.has(col.key);
                      const canHide = isVisible && visibleColumns.length <= 3;
                      return (
                        <label
                          key={col.key}
                          className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                            canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                          }`}>
                          <input
                            type="checkbox"
                            checked={isVisible}
                            onChange={() => toggleColumn(col.key)}
                            disabled={canHide}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                          />
                          <span className="flex-1">{col.label}</span>
                          {canHide && (
                            <span className="text-[10px] text-gray-400">Min 3 required</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div
                className="relative"
                ref={exportRef}
                title={
                  isLoading
                    ? 'Still loading all customers — export will include everything once loading finishes'
                    : undefined
                }>
                <DillButton
                  label="Export"
                  onClick={() => setIsExportOpen(!isExportOpen)}
                  icon={<Export size={14} />}
                  variant="primary"
                  isDisabled={isLoading}
                />

                {isExportOpen && (
                  <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                    <button
                      onClick={handleExportXlsx}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                      Export as XLSX
                    </button>
                    <button
                      onClick={handleExportCsv}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                      Export as CSV
                    </button>
                  </div>
                )}
              </div>
            </div>
          }
        />
      </div>

      {collectionActivityRow && (
        <DillModal
          open
          onClose={() => setCollectionActivityRow(null)}
          title={collectionActivityRow.businessName}
          subtitle={
            collectionActivityRow.quickbooksCustomerId
              ? `#${collectionActivityRow.quickbooksCustomerId}`
              : undefined
          }
          width="95vw"
          maxWidth="1200px"
          contentClassName="p-0 overflow-hidden">
          <div className="h-[75vh] px-5 py-4">
            <CollectionActivityTimeline buyerId={collectionActivityRow.id} hideActions />
          </div>
        </DillModal>
      )}
    </div>
  );
};
